import { createDeepEqualSelector } from 'Angular/state/selectors';

import {
    CORRELATION_HEATMAP_MODULES,
    EFFICIENT_FRONTIER_MODULE_TYPES,
    FIELD_TYPES_WITH_RELATED_ITEMS,
    MODULE_TYPES_WITH_RELATED_ITEMS,
    PAGE_SIZE_MAX,
    POSSIBLE_MODULE_SIZES,
    REPORT_TYPE_TO_NUM_REQUIRED_SECURITIES,
    TargetSecurityChoice,
} from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/constants';
import { Language } from 'Angular/enums/language.enum';
import { CustomPDFReportOrientation } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-orientation.enum';
import { CustomPDFReportType } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-type.enum';
import { CustomPDFReportModule } from '../interfaces/custom-pdf-report-module.interface';
import { CustomPDFReportBuilderStore } from './interfaces/custom-pdf-report-builder-store.interface';
import { CustomPdfReportAdvancedOptions } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/interfaces/custom-pdf-report-advanced-options.interface';
import { CustomPDFReportPreviewSecurities } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/interfaces/custom-pdf-report-preview-securities.interface';
import { CustomPDFReportSection } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/interfaces/custom-pdf-report-section.interface';
import { CustomPDFReportTab } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/interfaces/custom-pdf-report-tab.interface';
import { CustomPFDReportSaveParams } from 'CustomPDFReportsApp/ts/interfaces/custom-pdf-report-views.interface';

import { convertModulesToSaveState } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/utils/custom-pdf-report-builder-utils';
import {
    getSectionsWithModules,
    getSortedSections,
    hasValidReportSecurities,
} from 'CustomPDFReportsApp/ts/utils/custom-pdf-report-utils';

const getReportType = (state: CustomPDFReportBuilderStore): CustomPDFReportType | null =>
    (state && state.report.report_type) || null;

const getReportOrientation = (state: CustomPDFReportBuilderStore): CustomPDFReportOrientation | null =>
    (state && state.report.report_orientation) || null;

const getReportName = (state: CustomPDFReportBuilderStore): string => (state && state.report.name) || '';

const getReportDescription = (state: CustomPDFReportBuilderStore): string => (state && state.report.description) || '';

const getReportFooter = (state: CustomPDFReportBuilderStore): string | null =>
    (state && state.report.report_footer) || null;

const getReportModules = (state: CustomPDFReportBuilderStore): CustomPDFReportModule[][] =>
    (state && state.report.modules) || [];

const getDataSource = (state: CustomPDFReportBuilderStore): string => (state && state.report.data_source) || '';

const getPerformanceDisclosureFootnote = (state: CustomPDFReportBuilderStore): string =>
    (state && state.report.performance_disclosure_footnote) || '';

const getSections = (state: CustomPDFReportBuilderStore): CustomPDFReportSection[] => {
    const modules = (state && state.report.modules) || [];
    let sections = (state && state.report.sections) || [];
    sections = getSectionsWithModules(sections, modules);
    return getSortedSections(sections);
};

const getRemoveReportTitleHeader = (state: CustomPDFReportBuilderStore): boolean =>
    (state && state.report.remove_report_title_header) || false;

const getDisableInternalUseOnlyWatermark = (state: CustomPDFReportBuilderStore): boolean =>
    state?.report.disable_internal_use_only_watermark ?? true;

export const getAllowSectionOrdering = (state: CustomPDFReportBuilderStore): boolean =>
    state?.report.allow_section_ordering ?? true;

const getLanguage = (state: CustomPDFReportBuilderStore): string =>
    (state && state.report.language) || Language.ENGLISH;

const getIncludeBenchmarkDefault = (state: CustomPDFReportBuilderStore): boolean =>
    (state && state.report.include_benchmark_default) || false;

export const getAllowedModuleSizesByPage = createDeepEqualSelector(
    getReportModules,
    (modules: CustomPDFReportModule[][]): { [k: number]: number[] } => {
        const allowedModuleSizesByPage: { [k: number]: number[] } = {};
        modules.forEach((page, pageNum) => {
            const remainingSpace =
                PAGE_SIZE_MAX -
                page.reduce((acc, module) => {
                    return acc + module.module_size;
                }, 0);
            allowedModuleSizesByPage[pageNum] = POSSIBLE_MODULE_SIZES.filter((size) => size <= remainingSpace);
        });
        return allowedModuleSizesByPage;
    }
);

export const hasEfficientFrontierModules = createDeepEqualSelector(
    getReportModules,
    (modules: CustomPDFReportModule[][]): boolean => {
        return modules.some((page, _) =>
            page.some((module) => EFFICIENT_FRONTIER_MODULE_TYPES.includes(module.module_type))
        );
    }
);

const getAdvancedOptions = (state: CustomPDFReportBuilderStore): CustomPdfReportAdvancedOptions | null =>
    state.report.advanced_options;

export const getCustomPDFReportSaveParams = createDeepEqualSelector(
    getReportType,
    getReportOrientation,
    getReportName,
    getReportDescription,
    getReportFooter,
    getReportModules,
    getIncludeBenchmarkDefault,
    getAdvancedOptions,
    getDataSource,
    getPerformanceDisclosureFootnote,
    getSections,
    getRemoveReportTitleHeader,
    getAllowSectionOrdering,
    getDisableInternalUseOnlyWatermark,
    getLanguage,
    (
        report_type: CustomPDFReportType,
        report_orientation: CustomPDFReportOrientation,
        name: string,
        description: string,
        report_footer: string | null,
        modules: CustomPDFReportModule[][],
        include_benchmark_default: boolean,
        advanced_options: CustomPdfReportAdvancedOptions | null,
        data_source: string,
        performance_disclosure_footnote: string,
        sections: CustomPDFReportSection[],
        remove_report_title_header: boolean,
        allow_section_ordering: boolean,
        disable_internal_use_only_watermark: boolean,
        language: Language
    ): CustomPFDReportSaveParams => {
        return {
            report_type,
            report_orientation,
            name,
            description,
            report_footer,
            modules: convertModulesToSaveState(modules),
            include_benchmark_default,
            advanced_options,
            data_source,
            performance_disclosure_footnote,
            sections,
            remove_report_title_header,
            allow_section_ordering,
            disable_internal_use_only_watermark,
            language,
        };
    }
);

export const canSave = createDeepEqualSelector(
    getReportType,
    getReportModules,
    (reportType: CustomPDFReportType, modules: CustomPDFReportModule[][]): boolean => {
        // Must have selected a report type and have at least one module on the report in order to save it
        return !!reportType && modules.some((page) => page.length);
    }
);

export const getWarningMessage = createDeepEqualSelector(
    getReportModules,
    (modules: CustomPDFReportModule[][]): string => {
        const pagesMissingInput: number[] = [];
        modules.forEach((page, pageNum) => {
            page.forEach((module) => {
                if (
                    MODULE_TYPES_WITH_RELATED_ITEMS.has(module.module_type) &&
                    !Object.values(module.customizable_fields).some(
                        (field) => FIELD_TYPES_WITH_RELATED_ITEMS.has(field.type) && field.value
                    )
                ) {
                    pagesMissingInput.push(pageNum + 1);
                }
            });
        });
        let warningMessage: string = '';
        if (pagesMissingInput.length) {
            if (pagesMissingInput.length === 1) {
                warningMessage = `Page ${pagesMissingInput[0]} has a module with no attachment/security. This page will be blank when the report is generated.`;
            } else if (pagesMissingInput.length === 2) {
                warningMessage = `Pages ${pagesMissingInput[0]} and ${pagesMissingInput[1]} have a module with no attachment/security. These pages will be blank when the report is generated.`;
            } else {
                warningMessage = `Pages ${pagesMissingInput.slice(0, pagesMissingInput.length - 1).join(', ')}, and ${
                    pagesMissingInput[pagesMissingInput.length - 1]
                } have a module with no attachment/security. These pages will be blank when the report is generated.`;
            }
        }
        return warningMessage;
    }
);

const getPreviewSecurities = (state: CustomPDFReportBuilderStore): CustomPDFReportPreviewSecurities =>
    state.preview_securities;

export const getNumRequiredSecurities = (state: CustomPDFReportBuilderStore): number => {
    if (!state.report?.report_type || !(state.report.report_type in REPORT_TYPE_TO_NUM_REQUIRED_SECURITIES)) {
        return 0;
    }

    const reportTypeDefault =
        REPORT_TYPE_TO_NUM_REQUIRED_SECURITIES[
            state.report.report_type as keyof typeof REPORT_TYPE_TO_NUM_REQUIRED_SECURITIES
        ];

    // No modules yet (e.g. a brand-new report): fall back to the report type default, same as the backend.
    const modules = state.report.modules?.flat() ?? [];
    if (!modules.length) {
        return reportTypeDefault;
    }

    // Each module's own num_required_securities is computed by the backend, which already accounts for
    // module-specific overrides (e.g. saved-asset modules always requiring the report type default, or
    // modules that don't need a security at all). Take the max rather than re-deriving it here, so this
    // selector can't drift out of sync with the backend's per-module logic.
    //
    // One exception: target_security_choice is edited live in the builder (via the module editor modal)
    // without a round-trip to the backend, so a module's serialized num_required_securities can go stale
    // mid-session. Mirror the backend's short-circuits (base.py num_required_securities property) exactly.
    return modules.reduce((max, module) => {
        const targetSecurityChoice = module.customizable_fields?.target_security_choice?.value;
        const isReportSecuritiesCorrelation =
            CORRELATION_HEATMAP_MODULES.includes(module.module_type) &&
            module.customizable_fields?.target_correlation_choice?.value === 'report_securities';
        let effectiveNumRequiredSecurities;
        if (targetSecurityChoice === TargetSecurityChoice.SECONDARY_SECURITY) {
            effectiveNumRequiredSecurities = 2;
        } else if (targetSecurityChoice === TargetSecurityChoice.FIXED && !isReportSecuritiesCorrelation) {
            effectiveNumRequiredSecurities = 0;
        } else {
            effectiveNumRequiredSecurities = module.num_required_securities ?? 1;
        }
        return Math.max(max, effectiveNumRequiredSecurities);
    }, 0);
};

export const hasValidPreviewSecurities = createDeepEqualSelector(
    getReportType,
    getPreviewSecurities,
    (reportType: CustomPDFReportType | null, previewSecurities: CustomPDFReportPreviewSecurities) => {
        return hasValidReportSecurities(
            reportType,
            previewSecurities.primary,
            previewSecurities.secondary,
            previewSecurities.comparison
        );
    }
);

export const getActiveTabIndex = (tabs: CustomPDFReportTab[]): number => {
    return tabs.findIndex((tab) => tab.isActive);
};

export const getIsEdited = (state: CustomPDFReportBuilderStore): boolean => {
    const activeTabIndex = getActiveTabIndex(state.tabs);
    if (activeTabIndex === -1) {
        return false;
    }

    return state.tabs[activeTabIndex].isEdited;
};

export const getActiveTab = (tabs: CustomPDFReportTab[]): CustomPDFReportTab | null => {
    const activeTabIndex = getActiveTabIndex(tabs);
    if (activeTabIndex === -1) {
        return null;
    }

    return tabs[activeTabIndex];
};
